import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type CompetitionFormat } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { SideRounds } from '../components/BracketCanvas';
import { PublicPageToggle } from '../components/PublicPageToggle';
import { useWorkspace } from '../lib/workspace';
import { isAdminRole } from '../lib/roles';
import { useEffectiveRole } from '../lib/viewAs';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Trophy, GitBranch, RefreshCw } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Bracket — elimination comps (Queen/FlowState style). Winners bracket on top,
// losers bracket below it for double elim, grand final on the right. The
// rendering lives in components/BracketCanvas (shared with the public /watch
// spectator page); this page adds the admin affordances around it.
// ─────────────────────────────────────────────────────────────────────────────

function GeneratePanel({ compId, hasBracket, isElim }: { compId: string; hasBracket: boolean; isElim: boolean }) {
  const qc = useQueryClient();
  const [seedText, setSeedText] = useState('');
  const [format, setFormat] = useState<CompetitionFormat>('ELIMINATION_SINGLE');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => {
      const entrants = seedText.split('\n').map(s => s.trim()).filter(Boolean);
      return api.generateBracket(compId, {
        ...(entrants.length > 0 ? { entrants } : {}),
        ...(isElim ? {} : { format }),
        ...(hasBracket ? { force: true } : {}),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bracket', compId] }),
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  const go = () => {
    if (hasBracket && !window.confirm('Rebuild the bracket? All entered results will be discarded.')) return;
    setError('');
    mut.mutate();
  };

  return (
    <Card className={hasBracket ? 'mt-6' : ''}>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
        <GitBranch className="h-4 w-4 text-ink-meta" /> {hasBracket ? 'Rebuild bracket' : 'Generate bracket'}
      </h3>
      {!isElim && (
        <div className="mb-3">
          <label className="label">This comp has no elimination format yet — pick one (it will be saved on the comp):</label>
          <select value={format} onChange={e => setFormat(e.target.value as CompetitionFormat)}
            className="input text-xs">
            <option value="ELIMINATION_SINGLE">Single elimination</option>
            <option value="ELIMINATION_DOUBLE">Double elimination</option>
          </select>
        </div>
      )}
      <label className="label">
        Seed order — one team per line, best first. Leave empty to use enrolled clans.
      </label>
      <textarea
        value={seedText} onChange={e => setSeedText(e.target.value)} rows={4}
        placeholder={'Team Alpha\nTeam Bravo\n…'}
        className="input mb-3 w-full font-mono text-xs"
      />
      <button
        onClick={go} disabled={mut.isPending}
        className={cn('btn-primary flex items-center gap-2', MORPH)}
      >
        {hasBracket ? <RefreshCw className="h-3.5 w-3.5" /> : <GitBranch className="h-3.5 w-3.5" />}
        {mut.isPending ? 'Working…' : hasBracket ? 'Rebuild bracket' : 'Generate bracket'}
      </button>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </Card>
  );
}

export function Bracket() {
  const { activeComp, activeCompId, isLoading } = useWorkspace();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // View-as-aware role — every admin gate on this page reads THIS, not the
  // raw /me role, so a founder previewing a lower role actually loses the
  // write affordances (public-page toggle, bracket generate/rebuild, per-round
  // score entry) instead of keeping them live over a real customer's comp.
  // Declared before the early returns below to satisfy the Rules of Hooks.
  // Identity no-op whenever no view-as is active — see lib/viewAs.ts.
  const effectiveRole = useEffectiveRole(me?.role);
  const compId = activeCompId ?? '';

  const { data, isLoading: bracketLoading } = useQuery({
    queryKey: ['bracket', compId],
    queryFn: () => api.bracket(compId),
    enabled: !!compId,
    // Live-poll while any screenshot is mid-OCR so proposals appear unprompted.
    refetchInterval: (query) => {
      const shots = query.state.data?.shots ?? [];
      return shots.some(s => s.status === 'UPLOADED' || s.status === 'OCR_PROCESSING') ? 4000 : false;
    },
  });

  if (isLoading || (compId && bracketLoading)) return <PageSpinner />;

  if (!activeCompId) {
    return (
      <EmptyState
        title="No competition selected"
        message="Pick a competition from the sidebar to see its bracket."
        icon={<GitBranch className="h-12 w-12" strokeWidth={1.5} />}
      />
    );
  }

  const slots = data?.slots ?? [];
  const allShots = data?.shots ?? [];
  const admin = isAdminRole(effectiveRole);
  const format = data?.format ?? activeComp?.format ?? null;
  const isElim = format === 'ELIMINATION_SINGLE' || format === 'ELIMINATION_DOUBLE';
  const isDouble = format === 'ELIMINATION_DOUBLE';
  const isGroupStage = format === 'GROUP_STAGE'; // bracket comes from finalising the groups, not manual generation

  if (slots.length === 0 && !admin) {
    return (
      <EmptyState
        title="No bracket yet"
        message={isElim ? 'The bracket has not been generated yet — check back soon.' : format === 'GROUP_STAGE' ? 'The playoff bracket appears here once the group stage is finalised.' : 'This competition runs as a league, not an elimination bracket.'}
        icon={<GitBranch className="h-12 w-12" strokeWidth={1.5} />}
      />
    );
  }

  return (
    <div>
      <div className={cn('mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between', SETTLE)} style={settleDelay(0)}>
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink-meta">
            {isDouble ? 'Double Elimination' : 'Bracket'}
          </p>
          <h1 className="mt-1 truncate font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            {activeComp?.name || activeCompId.replace('COMP#', '')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {admin && (
            <PublicPageToggle compId={activeCompId} enabled={activeComp?.publicPageEnabled === true} />
          )}
          {data?.champion && (
            <div className="flex shrink-0 items-center gap-2 rounded-lg bg-accent-500/10 px-4 py-2">
              <Trophy className="h-4 w-4 text-accent-500" />
              <span className="text-sm font-bold text-accent-500">{data.champion}</span>
            </div>
          )}
        </div>
      </div>

      {slots.length === 0 ? (
        <div className={SETTLE} style={settleDelay(1)}>
          {isGroupStage ? (
            <EmptyState
              title="Playoff bracket not generated yet"
              message="For a Groups → Single Elimination comp, the bracket is created automatically when you finalise the group stage — head to Groups."
              icon={<GitBranch className="h-12 w-12" strokeWidth={1.5} />}
            />
          ) : (
            <GeneratePanel compId={activeCompId} hasBracket={false} isElim={isElim} />
          )}
        </div>
      ) : (
        <>
          <div className={cn('flex flex-col gap-8 xl:flex-row xl:items-stretch', SETTLE)} style={settleDelay(1)}>
            <div className="min-w-0 flex-1 space-y-8">
              {isDouble && (
                <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-meta">Winners bracket</p>
              )}
              <SideRounds compId={activeCompId} slots={slots} shots={allShots} side="WINNERS" canEnter={admin} />
              {isDouble && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-meta">Losers bracket</p>
                  <SideRounds compId={activeCompId} slots={slots} shots={allShots} side="LOSERS" canEnter={admin} />
                </>
              )}
            </div>
            {slots.some(s => s.side === 'GRAND_FINAL') && (
              <div className="shrink-0">
                <SideRounds compId={activeCompId} slots={slots} shots={allShots} side="GRAND_FINAL" canEnter={admin} />
              </div>
            )}
          </div>
          {admin && !isGroupStage && (
            <div className={SETTLE} style={settleDelay(2)}>
              <GeneratePanel compId={activeCompId} hasBracket isElim={isElim} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
